import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import Combobox from '@/components/combo-box';
import { RichTextEditor } from '@/components/rich-text';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTenant } from '@/hooks';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { toLexicalJSON } from '@/lib/lexical-utils';
import { cn, stripHtml } from '@/lib/utils';
import {
  useAddEventWithInvitationsMutation,
  useDeleteEventWithInvitationsMutation,
  useGetAllClientUserListQuery,
  useGetCurrentUserQuery,
  useGetUserAllCalendarTasksQuery,
  useUpdateEventWithInvitationsMutation,
} from '@/state';
import { useGetBoardsQuery, useGetCoursesQuery } from '@/state/course';
import { useGetInternalProjectsQuery } from '@/state/project';
import type { AddCalendarEventPayload, ApiErrorResponse } from '@/types';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  addMinutes,
  addMonths,
  addWeeks,
  endOfWeek,
  format,
  isValid,
  isSameMonth,
  isWithinInterval,
  subMonths,
  subWeeks,
  startOfWeek,
} from 'date-fns';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  CALENDAR_EVENT_META,
  getEventDate,
  normalizeCalendarEvents,
  type CalendarEventItem,
  type CalendarEventType,
} from './calendar-utils';
import { EventCalendarDayButton } from './components/event-calendar-day-button';
import { EventList } from './components/event-list';
import { EventViewDialog } from './components/event-view-dialog';

type MeetingForType = 'users' | 'boards' | 'projects' | 'courses' | 'allUsers';

const getUserLabel = (user: {
  profile?: { first_name?: string; last_name?: string };
  email?: string;
  id: string;
}) => {
  const firstName = user.profile?.first_name?.trim() ?? '';
  const lastName = user.profile?.last_name?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || user.email || user.id;
};

const formatDateTimeLocalInput = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm");

const toUtcIsoFromDateTimeLocal = (dateTimeLocal: string) => {
  if (!dateTimeLocal) return null;
  const localDate = new Date(dateTimeLocal);
  if (!isValid(localDate)) return null;
  return localDate.toISOString();
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const CalendarPage = () => {
  const { tenant } = useTenant();
  const { isStudent, hasSyncModule } = useClientPermissions();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEventItem | null>(null);
  const [deletingEventId, setDeletingEventId] = useState<number | null>(null);
  const [editingEventId, setEditingEventId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'monthly' | 'weekly'>('monthly');
  const [eventDialogMode, setEventDialogMode] = useState<'create' | 'edit'>('create');
  const [isDateFilterActive, setIsDateFilterActive] = useState(false);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [eventName, setEventName] = useState('');
  const [startDateTimeInput, setStartDateTimeInput] = useState(
    formatDateTimeLocalInput(new Date())
  );
  const [endDateTimeInput, setEndDateTimeInput] = useState(
    formatDateTimeLocalInput(addMinutes(new Date(), 5))
  );
  const [meetingFor, setMeetingFor] = useState<MeetingForType>('users');
  const [isOnlineMeeting, setIsOnlineMeeting] = useState(true);
  const [descriptionHtml, setDescriptionHtml] = useState('');

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedBoardIds, setSelectedBoardIds] = useState<number[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([]);
  const [selectedUsersPicker, setSelectedUsersPicker] = useState('');
  const [selectedBoardsPicker, setSelectedBoardsPicker] = useState('');
  const [selectedProjectsPicker, setSelectedProjectsPicker] = useState('');
  const [selectedCoursesPicker, setSelectedCoursesPicker] = useState('');

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetUserAllCalendarTasksQuery(tenant ? { tenant } : skipToken);

  const { data: currentUser } = useGetCurrentUserQuery(tenant ? { tenant } : skipToken);

  const { data: usersData = [], isFetching: isUsersFetching } =
    useGetAllClientUserListQuery(
      tenant && isCreateDialogOpen && meetingFor === 'users' ? { tenant } : skipToken
    );

  const { data: boardsData = [], isFetching: isBoardsFetching } = useGetBoardsQuery(
    tenant && isCreateDialogOpen && meetingFor === 'boards' ? { tenant } : skipToken
  );

  const { data: projectsData = [], isFetching: isProjectsFetching } =
    useGetInternalProjectsQuery(
      tenant && isCreateDialogOpen && meetingFor === 'projects'
        ? { tenant }
        : skipToken
    );

  const { data: coursesData = [], isFetching: isCoursesFetching } = useGetCoursesQuery(
    tenant && isCreateDialogOpen && meetingFor === 'courses'
      ? { tenant }
      : skipToken
  );

  const [addEventWithInvitations, { isLoading: isSubmittingEvent }] =
    useAddEventWithInvitationsMutation();
  const [updateEventWithInvitations, { isLoading: isUpdatingEvent }] =
    useUpdateEventWithInvitationsMutation();
  const [deleteEventWithInvitations] = useDeleteEventWithInvitationsMutation();

  const allEvents = useMemo(() => normalizeCalendarEvents(data), [data]);

  const sortedEvents = useMemo(() => {
    const seen = new Set<string>();
    const uniqueEvents = allEvents.filter((event) => {
      const key = `${event.type}-${event.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return [...uniqueEvents].sort((a, b) => {
      const left = getEventDate(a)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const right = getEventDate(b)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return left - right;
    });
  }, [allEvents]);

  const weekEvents = useMemo(() => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });

    return sortedEvents.filter((event) => {
      const eventDate = getEventDate(event);
      if (!eventDate) return false;

      return isWithinInterval(eventDate, {
        start: weekStart,
        end: weekEnd,
      });
    });
  }, [selectedDate, sortedEvents]);

  const monthEvents = useMemo(() => {
    return sortedEvents.filter((event) => {
      const eventDate = getEventDate(event);
      if (!eventDate) return false;
      return isSameMonth(eventDate, selectedDate);
    });
  }, [selectedDate, sortedEvents]);

  const selectedDateEvents = useMemo(() => {
    return sortedEvents.filter((event) => {
      const eventDate = getEventDate(event);
      if (!eventDate) return false;
      return (
        eventDate.getFullYear() === selectedDate.getFullYear() &&
        eventDate.getMonth() === selectedDate.getMonth() &&
        eventDate.getDate() === selectedDate.getDate()
      );
    });
  }, [selectedDate, sortedEvents]);

  const dateModifiers = useMemo(() => {
    const modifierMap: Record<
      'personalTask' | 'milestoneTask' | 'projectTask' | 'invitedTask',
      Date[]
    > = {
      personalTask: [],
      milestoneTask: [],
      projectTask: [],
      invitedTask: [],
    };

    const pushDateByType = (type: CalendarEventType, date: Date) => {
      if (type === 'personal') modifierMap.personalTask.push(date);
      if (type === 'milestone') modifierMap.milestoneTask.push(date);
      if (type === 'project') modifierMap.projectTask.push(date);
      if (type === 'invited') modifierMap.invitedTask.push(date);
    };

    allEvents.forEach((event) => {
      const eventDate = getEventDate(event);
      if (!eventDate) return;
      pushDateByType(event.type, eventDate);
    });

    return modifierMap;
  }, [allEvents]);

  const activeWeekRange = useMemo(
    () => ({
      from: startOfWeek(selectedDate, { weekStartsOn: 1 }),
      to: endOfWeek(selectedDate, { weekStartsOn: 1 }),
    }),
    [selectedDate]
  );

  const userOptions = useMemo(() => {
    return usersData
      .filter((user) => !selectedUserIds.includes(user.id))
      .map((user) => ({
        value: user.id,
        label: getUserLabel(user),
        extras: (
          <Avatar className="mr-2 h-5 w-5">
            <AvatarImage src={user.profile?.thumbnail || user.profile?.photo_url || ''} />
            <AvatarFallback>{getUserLabel(user).slice(0, 1)}</AvatarFallback>
          </Avatar>
        ),
      }));
  }, [usersData, selectedUserIds]);

  const boardOptions = useMemo(() => {
    return boardsData
      .filter((board) => !selectedBoardIds.includes(board.id))
      .map((board) => ({
        value: String(board.id),
        label: board.board_name,
      }));
  }, [boardsData, selectedBoardIds]);

  const projectOptions = useMemo(() => {
    return projectsData
      .filter((project) => !selectedProjectIds.includes(project.id))
      .map((project) => ({
        value: String(project.id),
        label: project.name,
      }));
  }, [projectsData, selectedProjectIds]);

  const courseOptions = useMemo(() => {
    return coursesData
      .filter((course) => !selectedCourseIds.includes(course.id))
      .map((course) => ({
        value: String(course.id),
        label: course.course_name,
      }));
  }, [coursesData, selectedCourseIds]);

  const hasTenant = Boolean(tenant);

  useEffect(() => {
    if (!isCreateDialogOpen || meetingFor !== 'users' || !currentUser?.id) return;
    if (selectedUserIds.includes(currentUser.id)) return;
    setSelectedUserIds((prev) => [...prev, currentUser.id]);
  }, [currentUser?.id, isCreateDialogOpen, meetingFor, selectedUserIds]);

  useEffect(() => {
    if (!isStudent) return;
    if (meetingFor !== 'users') setMeetingFor('users');
  }, [isStudent, meetingFor]);


  const resetCreateForm = (date: Date) => {
    const endDate = addMinutes(date, 5);
    setEventName('');
    setStartDateTimeInput(formatDateTimeLocalInput(date));
    setEndDateTimeInput(formatDateTimeLocalInput(endDate));
    setMeetingFor('users');
    setIsOnlineMeeting(true);
    setDescriptionHtml('');
    setSelectedBoardIds([]);
    setSelectedProjectIds([]);
    setSelectedCourseIds([]);
    setSelectedUserIds(currentUser?.id ? [currentUser.id] : []);
    setSelectedUsersPicker('');
    setSelectedBoardsPicker('');
    setSelectedProjectsPicker('');
    setSelectedCoursesPicker('');
  };

  const openCreateDialogForDate = (date: Date) => {
    setEventDialogMode('create');
    setEditingEventId(null);
    setSelectedDate(date);
    resetCreateForm(date);
    setIsCreateDialogOpen(true);
  };

  const openEditDialogForEvent = (event: CalendarEventItem) => {
    if (!(event.type === 'personal' || event.type === 'invited')) return;

    setEventDialogMode('edit');
    setEditingEventId(event.id);
    setSelectedEvent(null);

    const startDate = event.startDate ? new Date(event.startDate) : new Date();
    const endDate = event.endDate ? new Date(event.endDate) : addMinutes(startDate, 5);

    setEventName(event.title ?? '');
    setStartDateTimeInput(formatDateTimeLocalInput(startDate));
    setEndDateTimeInput(formatDateTimeLocalInput(endDate));
    setMeetingFor('users');
    const meetingType =
      isStudent || !hasSyncModule
        ? 1
        : 'meeting_type' in event.source
          ? (event.source.meeting_type ?? 1)
          : 1;
    setIsOnlineMeeting(meetingType === 1);
    setDescriptionHtml(event.description ? `<p>${escapeHtml(event.description)}</p>` : '');

    const invitedUserIds =
      'invited_users' in event.source
        ? event.source.invited_users.map((item) => item.user)
        : [];
    const withCurrent =
      currentUser?.id != null
        ? Array.from(new Set([...invitedUserIds, currentUser.id]))
        : invitedUserIds;
    setSelectedUserIds(withCurrent);

    setSelectedBoardIds([]);
    setSelectedProjectIds([]);
    setSelectedCourseIds([]);
    setSelectedUsersPicker('');
    setSelectedBoardsPicker('');
    setSelectedProjectsPicker('');
    setSelectedCoursesPicker('');
    setIsCreateDialogOpen(true);
  };

  const toggleNumericSelection = (
    value: number,
    values: number[],
    setValues: (next: number[]) => void
  ) => {
    if (values.includes(value)) {
      setValues(values.filter((id) => id !== value));
      return;
    }
    setValues([...values, value]);
  };

  const handleToggleUser = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      if (currentUser?.id === userId) return;
      setSelectedUserIds(selectedUserIds.filter((id) => id !== userId));
      return;
    }
    setSelectedUserIds([...selectedUserIds, userId]);
  };

  const buildPayload = (): AddCalendarEventPayload | null => {
    const startDateIso = toUtcIsoFromDateTimeLocal(startDateTimeInput);
    const endDateIso = toUtcIsoFromDateTimeLocal(endDateTimeInput);

    if (!startDateIso || !endDateIso) {
      toast.error('Please enter valid date and time.');
      return null;
    }

    if (
      eventDialogMode === 'create' &&
      new Date(startDateIso).getTime() <= new Date().getTime()
    ) {
      toast.error('Start date/time cannot be in the past.');
      return null;
    }

    if (new Date(endDateIso).getTime() <= new Date(startDateIso).getTime()) {
      toast.error('End date/time must be after start date/time.');
      return null;
    }

    if (!eventName.trim()) {
      toast.error('Event name is required.');
      return null;
    }

    const payload: AddCalendarEventPayload = {
      title: eventName.trim(),
      start_date: startDateIso,
      end_date: endDateIso,
      description: toLexicalJSON(stripHtml(descriptionHtml)),
      meeting_type: isStudent || !hasSyncModule ? 1 : isOnlineMeeting ? 1 : 2,
      invited_users: [],
      all_users: false,
    };

    if (meetingFor === 'users') {
      const finalUserIds = currentUser?.id
        ? Array.from(new Set([...selectedUserIds, currentUser.id]))
        : selectedUserIds;

      if (finalUserIds.length === 0) {
        toast.error('Select at least one attendee.');
        return null;
      }

      payload.invited_users = finalUserIds.map((userId) => ({ user: userId }));
      return payload;
    }

    if (isStudent) {
      toast.error('Students can create events only by selecting users.');
      return null;
    }

    payload.invited_users = [];

    if (meetingFor === 'boards') {
      if (selectedBoardIds.length === 0) {
        toast.error('Select at least one board.');
        return null;
      }
      payload.board = selectedBoardIds;
      return payload;
    }

    if (meetingFor === 'projects') {
      if (selectedProjectIds.length === 0) {
        toast.error('Select at least one project.');
        return null;
      }
      payload.project = selectedProjectIds;
      return payload;
    }

    if (meetingFor === 'courses') {
      if (selectedCourseIds.length === 0) {
        toast.error('Select at least one course.');
        return null;
      }
      payload.course = selectedCourseIds;
      return payload;
    }

    payload.all_users = true;
    return payload;
  };

  const handleSubmitEvent = async () => {
    if (!tenant) return;

    const payload = buildPayload();
    if (!payload) return;

    try {
      if (eventDialogMode === 'edit') {
        if (editingEventId == null) {
          toast.error('No editable event selected.');
          return;
        }
        await updateEventWithInvitations({
          tenant,
          eventId: editingEventId,
          data: payload,
        }).unwrap();
        toast.success('Event updated successfully.');
      } else {
        await addEventWithInvitations({ tenant, data: payload }).unwrap();
        toast.success('Event created successfully.');
      }
      setIsCreateDialogOpen(false);
      refetch();
    } catch {
      toast.error(eventDialogMode === 'edit' ? 'Failed to update event.' : 'Failed to create event.');
    }
  };

  const handleDeleteEvent = async (event: CalendarEventItem) => {
    if (!tenant || !(event.type === 'personal' || event.type === 'invited')) return;
    try {
      setDeletingEventId(event.id);
      await deleteEventWithInvitations({
        tenant,
        eventId: event.id,
      }).unwrap();
      toast.success('Event deleted successfully.');
      if (selectedEvent?.id === event.id && selectedEvent?.type === event.type) {
        setSelectedEvent(null);
      }
      refetch();
    } catch {
      toast.error('Failed to delete event.');
    } finally {
      setDeletingEventId(null);
    }
  };

  const navigateByView = (direction: 'prev' | 'next') => {
    setIsDateFilterActive(false);
    if (viewMode === 'monthly') {
      setSelectedDate((prev) =>
        direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1)
      );
      return;
    }
    setSelectedDate((prev) =>
      direction === 'prev' ? subWeeks(prev, 1) : addWeeks(prev, 1)
    );
  };

  return (
    <WithLoadingErrorWrapper
      loading={isLoading}
      isFetching={isFetching}
      error={error as ApiErrorResponse}
      onRetry={refetch}
      className="mx-auto w-full max-w-screen-2xl space-y-6"
    >
      {!hasTenant && (
        <Card className="border-dashed">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              Select an organization to view your calendar.
            </p>
          </CardContent>
        </Card>
      )}

      {hasTenant && (
        <>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
              <h1 className="text-2xl font-semibold tracking-tight">Calendar</h1>
              <p className="text-sm text-muted-foreground">
                Personal tasks, milestones, project deadlines, and invited events.
              </p>
              </div>
              <Button type="button" size="sm" onClick={() => openCreateDialogForDate(new Date())}>
                Add event
              </Button>
            </div>

            <Tabs
              value={viewMode}
              onValueChange={(value) => {
                setViewMode(value as 'monthly' | 'weekly');
                setIsDateFilterActive(false);
              }}
              className="w-full"
            >
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                  <TabsList className="w-fit">
                    <TabsTrigger value="monthly">Monthly View</TabsTrigger>
                    <TabsTrigger value="weekly">Weekly View</TabsTrigger>
                  </TabsList>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={viewMode === 'monthly' ? 'Previous month' : 'Previous week'}
                    onClick={() => navigateByView('prev')}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedDate(new Date());
                      setIsDateFilterActive(false);
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={viewMode === 'monthly' ? 'Next month' : 'Next week'}
                    onClick={() => navigateByView('next')}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Tabs>
          </div>

          <div className="grid items-start gap-6 xl:grid-cols-[minmax(350px,440px)_1fr]">
            <div className="w-full">
              <div className="h-fit w-full self-start">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CalendarDays className="h-5 w-5" />
                  Select Date
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-start overflow-x-auto">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    month={selectedDate}
                    onMonthChange={(month) => setSelectedDate(month)}
                    onSelect={(date) => {
                      if (date) {
                        setSelectedDate(date);
                      }
                    }}
                    onDayClick={(day) => {
                      setSelectedDate(day);
                      setIsDateFilterActive(true);
                    }}
                    modifiers={{
                      ...dateModifiers,
                      activeWeek: viewMode === 'weekly' ? activeWeekRange : undefined,
                    }}
                    modifiersClassNames={{
                      activeWeek:
                        'bg-primary/10 rounded-md data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground',
                    }}
                    components={{ DayButton: EventCalendarDayButton }}
                    className="rounded-md border [--cell-size:2.5rem] sm:[--cell-size:2.9rem]"
                  />
                </div>

                <p className="text-xs text-muted-foreground">
                  Click a date to filter the event list for that day.
                </p>
                {viewMode === 'weekly' && (
                  <p className="text-xs text-primary">
                    Highlighted dates are the current week shown in the events list.
                  </p>
                )}

                <div className="grid gap-2">
                  {(Object.keys(CALENDAR_EVENT_META) as CalendarEventType[]).map(
                    (eventType) => (
                      <div key={eventType} className="flex items-center gap-2 text-sm">
                        <span
                          className={cn(
                            'inline-block h-2.5 w-2.5 rounded-full',
                            CALENDAR_EVENT_META[eventType].dotClassName
                          )}
                        />
                        <span className="text-muted-foreground">
                          {CALENDAR_EVENT_META[eventType].label}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </CardContent>
              </div>
            </div>

            <div className="w-full min-w-0 flex-1">
              <div className="mt-4">
                <EventList
                  title={
                    isDateFilterActive
                      ? `Events on ${format(selectedDate, 'MMMM d, yyyy')}`
                      : viewMode === 'monthly'
                        ? 'Events This Month'
                        : 'Events This Week'
                  }
                  events={
                    isDateFilterActive
                      ? selectedDateEvents
                      : viewMode === 'monthly'
                        ? monthEvents
                        : weekEvents
                  }
                  onOpenDetail={(event) => setSelectedEvent(event)}
                  onEditEvent={openEditDialogForEvent}
                  onDeleteEvent={handleDeleteEvent}
                  deletingEventId={deletingEventId}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <EventViewDialog
        event={selectedEvent}
        onOpenChange={(open) => {
          if (!open) setSelectedEvent(null);
        }}
      />

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) setEditingEventId(null);
        }}
      >
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {eventDialogMode === 'edit' ? 'Edit event' : 'Event details'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid gap-2">
              <label className="text-sm font-medium" htmlFor="event-name">
                Event name *
              </label>
              <Input
                id="event-name"
                placeholder="Enter event name"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
              />
            </div>

            <div className="grid gap-3 md:grid-cols-[1fr_32px_1fr] md:items-end">
              <div className="grid gap-2">
                <label className="text-sm font-medium" htmlFor="start-date-time">
                  Start date & time
                </label>
                <Input
                  id="start-date-time"
                  type="datetime-local"
                  value={startDateTimeInput}
                  onChange={(e) => setStartDateTimeInput(e.target.value)}
                />
              </div>

              <div className="hidden pb-2 text-center text-xl text-muted-foreground md:block">
                →
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium" htmlFor="end-date-time">
                  End date & time
                </label>
                <Input
                  id="end-date-time"
                  type="datetime-local"
                  value={endDateTimeInput}
                  onChange={(e) => setEndDateTimeInput(e.target.value)}
                />
              </div>
            </div>

            {!isStudent && (
              <div className="grid gap-2">
                <label className="text-sm font-medium">Meeting for</label>
                <Tabs
                  value={meetingFor}
                  onValueChange={(value) => {
                    setMeetingFor(value as MeetingForType);
                  }}
                >
                  <TabsList className="h-auto w-fit flex-wrap justify-start">
                    <TabsTrigger value="users">Users</TabsTrigger>
                    <TabsTrigger value="boards">Boards</TabsTrigger>
                    <TabsTrigger value="projects">Projects</TabsTrigger>
                    <TabsTrigger value="courses">Courses</TabsTrigger>
                    <TabsTrigger value="allUsers">All Users</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            )}

            {meetingFor !== 'allUsers' && (
              <div className="grid gap-3">
                <label className="text-sm font-medium">
                  {meetingFor === 'users' ? 'Attendees' : 'Select'}
                </label>
                {meetingFor === 'users' && (
                  <>
                    <Combobox
                      options={userOptions}
                      selectedOption={selectedUsersPicker}
                      onSelect={(value) => {
                        setSelectedUsersPicker('');
                        handleToggleUser(value);
                      }}
                      placeholder={isUsersFetching ? 'Loading users...' : 'Search user'}
                      searchable
                    />
                    <div className="flex flex-wrap gap-2">
                      {selectedUserIds.map((userId) => {
                        const user = usersData.find((item) => item.id === userId);
                        const label = user ? getUserLabel(user) : userId;
                        const isSelf = currentUser?.id === userId;
                        return (
                          <div
                            key={userId}
                            className="flex max-w-full items-center gap-2 rounded-full border bg-muted/30 px-2 py-1 text-sm"
                          >
                            <Avatar className="h-6 w-6">
                              <AvatarImage
                                src={user?.profile?.thumbnail || user?.profile?.photo_url || ''}
                              />
                              <AvatarFallback>{label.slice(0, 1)}</AvatarFallback>
                            </Avatar>
                            <span className="max-w-40 truncate">{label}</span>
                            {!isSelf ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedUserIds(
                                    selectedUserIds.filter((id) => id !== userId)
                                  )
                                }
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            ) : (
                              <span className="text-xs text-muted-foreground">(You)</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {meetingFor === 'boards' && (
                  <>
                    <Combobox
                      options={boardOptions}
                      selectedOption={selectedBoardsPicker}
                      onSelect={(value) => {
                        setSelectedBoardsPicker('');
                        const numericId = Number(value);
                        toggleNumericSelection(
                          numericId,
                          selectedBoardIds,
                          setSelectedBoardIds
                        );
                      }}
                      placeholder={isBoardsFetching ? 'Loading boards...' : 'Search board'}
                      searchable
                    />
                    <div className="flex flex-wrap gap-2">
                      {selectedBoardIds.map((boardId) => {
                        const board = boardsData.find((item) => item.id === boardId);
                        return (
                          <div
                            key={boardId}
                            className="flex max-w-full items-center gap-2 rounded-full border bg-muted/30 px-3 py-1 text-sm"
                          >
                            <span className="max-w-44 truncate">
                              {board?.board_name || `Board ${boardId}`}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedBoardIds(
                                  selectedBoardIds.filter((id) => id !== boardId)
                                )
                              }
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {meetingFor === 'projects' && (
                  <>
                    <Combobox
                      options={projectOptions}
                      selectedOption={selectedProjectsPicker}
                      onSelect={(value) => {
                        setSelectedProjectsPicker('');
                        const numericId = Number(value);
                        toggleNumericSelection(
                          numericId,
                          selectedProjectIds,
                          setSelectedProjectIds
                        );
                      }}
                      placeholder={isProjectsFetching ? 'Loading projects...' : 'Search project'}
                      searchable
                    />
                    <div className="flex flex-wrap gap-2">
                      {selectedProjectIds.map((projectId) => {
                        const project = projectsData.find((item) => item.id === projectId);
                        return (
                          <div
                            key={projectId}
                            className="flex max-w-full items-center gap-2 rounded-full border bg-muted/30 px-2 py-1 text-sm"
                          >
                            <Avatar className="h-6 w-6 rounded-md">
                              <AvatarImage src={project?.project_banner || ''} />
                              <AvatarFallback>P</AvatarFallback>
                            </Avatar>
                            <span className="max-w-40 truncate">
                              {project?.name || `Project ${projectId}`}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedProjectIds(
                                  selectedProjectIds.filter((id) => id !== projectId)
                                )
                              }
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {meetingFor === 'courses' && (
                  <>
                    <Combobox
                      options={courseOptions}
                      selectedOption={selectedCoursesPicker}
                      onSelect={(value) => {
                        setSelectedCoursesPicker('');
                        const numericId = Number(value);
                        toggleNumericSelection(
                          numericId,
                          selectedCourseIds,
                          setSelectedCourseIds
                        );
                      }}
                      placeholder={isCoursesFetching ? 'Loading courses...' : 'Search course'}
                      searchable
                    />
                    <div className="flex flex-wrap gap-2">
                      {selectedCourseIds.map((courseId) => {
                        const course = coursesData.find((item) => item.id === courseId);
                        return (
                          <div
                            key={courseId}
                            className="flex max-w-full items-center gap-2 rounded-full border bg-muted/30 px-2 py-1 text-sm"
                          >
                            <Avatar className="h-6 w-6 rounded-md">
                              <AvatarImage src={course?.course_banner || ''} />
                              <AvatarFallback>C</AvatarFallback>
                            </Avatar>
                            <span className="max-w-40 truncate">
                              {course?.course_name || `Course ${courseId}`}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedCourseIds(
                                  selectedCourseIds.filter((id) => id !== courseId)
                                )
                              }
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {!isStudent && hasSyncModule && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium">Online meeting</label>
                <Checkbox
                  checked={isOnlineMeeting}
                  onCheckedChange={(checked) => setIsOnlineMeeting(Boolean(checked))}
                />
              </div>
            )}

            <div className="grid gap-2">
              <label className="text-sm font-medium" htmlFor="event-description">
                Description
              </label>
              <div className="rounded-lg border border-border/50 bg-muted/10 p-1">
                <RichTextEditor
                  value={descriptionHtml}
                  onChange={(value) => setDescriptionHtml(value)}
                  minHeight={180}
                  placeholder="Add meeting details"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSubmitEvent}
                disabled={isSubmittingEvent || isUpdatingEvent}
              >
                {isSubmittingEvent || isUpdatingEvent
                  ? eventDialogMode === 'edit'
                    ? 'Updating...'
                    : 'Creating...'
                  : eventDialogMode === 'edit'
                    ? 'Update'
                    : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </WithLoadingErrorWrapper>
  );
};

export default CalendarPage;
